/**
 * Decision 0149: runner resources. What a runner's sampler measured: the machine's load now (heartbeat `load`) and a
 * run's peaks and averages at its end (Final `resources`). Numbers and enums only; never a process name, a path or a
 * command line.
 *
 * Wire tolerance, both ways: the hub RPC fields are plain JSON (`Schema.Json`) and the cloud reads them with the sanitizers
 * below, so a malformed or newer value never fails a heartbeat or a Final (an old cloud's decoder drops the unknown
 * keys). What the cloud stores (`RunEnded.resources`, `Run.resources`) is the strict schema, written by the sanitizer.
 */
import { Schema } from "effect"
import { NonNegInt } from "./ids"

/** Memory pressure: macOS `kern.memorystatus_vm_pressure_level` 1/2/4; Linux PSI memory `some avg10` (< 10, < 40, ≥ 40). */
export const LOAD_PRESSURES = ["normal", "warn", "critical"] as const
export const LoadPressure = Schema.Literals(LOAD_PRESSURES)
export type LoadPressure = typeof LoadPressure.Type

const Num = Schema.Number.check(Schema.isFinite(), Schema.isGreaterThanOrEqualTo(0))

/** One run's share of the machine right now (its process tree from the CLI's pid). */
export const RunLoad = Schema.Struct({
  runId: Schema.String,
  /** CPU of the run's process tree, % of one core (400 = four cores busy). */
  cpuPct: Num,
  rssMb: Num,
})
export type RunLoad = typeof RunLoad.Type

/**
 * The machine's load at one sample (heartbeat `load`). A local runner reports the whole machine (every process, not
 * only Minga's); a Platform sandbox its container (cgroup and the size the Worker started it with).
 */
export const RunnerLoad = Schema.Struct({
  /** When the sample was taken (runner clock). */
  at: Num,
  /** CPUs the OS shows (`nproc`, `os.cpus()`). */
  cores: Num,
  /** The real CPU quota when it is below `cores` (cgroup `cpu.max`, the sandbox's instance size): 0.5 on standard-1. */
  vcpu: Schema.optionalKey(Num),
  /** Memory of the machine, or the cgroup or sandbox limit. */
  memMb: Num,
  /** Busy CPU, % of the whole machine (or of the quota): 0..100. */
  cpuPct: Num,
  /** Memory in use (total − available), % of `memMb`. */
  memPct: Num,
  pressure: LoadPressure,
  swapUsedMb: Schema.optionalKey(Num),
  /** Linux PSI `cpu some avg10` (% of time some task waited for a CPU). */
  cpuWaitPct: Schema.optionalKey(Num),
  /** cgroup `cpu.stat`: share of scheduler periods throttled since the last sample, 0..100. */
  throttledPct: Schema.optionalKey(Num),
  /** 1-minute load average (0 on Windows). */
  load1: Schema.optionalKey(Num),
  runs: Schema.optionalKey(Schema.Array(RunLoad)),
})
export type RunnerLoad = typeof RunnerLoad.Type

/** A run's resources at its end (Final `resources` → `RunEnded.resources` → `Run.resources`). */
export const RunResources = Schema.Struct({
  samples: NonNegInt,
  /** The run's process tree: peak and average CPU, % of one core. */
  cpuPeakPct: Num,
  cpuAvgPct: Num,
  rssPeakMb: Num,
  rssAvgMb: Num,
  /** The machine it ran on: CPUs shown, the real quota (sandbox, cgroup), memory. */
  cores: Num,
  vcpu: Schema.optionalKey(Num),
  memMb: Num,
  /** The whole machine during the run (every process): busy CPU % and memory used %. */
  machineCpuPeakPct: Num,
  machineCpuAvgPct: Num,
  machineMemPeakPct: Num,
  pressurePeak: LoadPressure,
  swapPeakMb: Schema.optionalKey(Num),
  /** cgroup: share of the run's scheduler periods that were throttled, 0..100. */
  throttledPct: Schema.optionalKey(Num),
  /** cgroup `memory.events` `oom_kill` during the run. */
  oomKills: Schema.optionalKey(NonNegInt),
  /** macOS: CPU of shared services the run's builds started (Simulator, Xcode build service), % of one core. */
  sharedCpuPeakPct: Schema.optionalKey(Num),
  /** What the machine is: "darwin", "linux", "linux-sandbox", "windows". */
  host: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(20))),
})
export type RunResources = typeof RunResources.Type

/**
 * A runner's load as the runner pages and Settings › Runners show it (`GET /api/workspaces/:ws/runners` `load`): now,
 * the worst minute of the last hour, and a state. Overloaded = sustained memory pressure, throttling or a pinned CPU
 * (core `loadSummary`); a state, never a limit. The web decodes it on its own, so an odd value never hides the list.
 */
export const RunnerLoadView = Schema.Struct({
  state: Schema.Literals(["normal", "busy", "overloaded"]),
  /** Why it is Overloaded ("memory pressure critical for 6 min"); null otherwise. */
  reason: Schema.NullOr(Schema.String),
  since: Schema.NullOr(Num),
  at: Num,
  cores: Num,
  vcpu: Schema.optionalKey(Num),
  memMb: Num,
  cpuPct: Num,
  memPct: Num,
  pressure: LoadPressure,
  swapUsedMb: Schema.optionalKey(Num),
  throttledPct: Schema.optionalKey(Num),
  load1: Schema.optionalKey(Num),
  runs: Schema.optionalKey(Schema.Array(RunLoad)),
  peaks: Schema.Struct({
    windowMin: Num,
    cpuPct: Num,
    memPct: Num,
    pressure: LoadPressure,
    throttledPct: Schema.optionalKey(Num),
  }),
})
export type RunnerLoadView = typeof RunnerLoadView.Type

// #region sanitizers (pure; never throw)
const isObj = (u: unknown): u is Record<string, unknown> =>
  typeof u === "object" && u !== null && !Array.isArray(u)

const MAX_PCT = 100_000 // a 1000-core machine at full tilt; anything above is noise
const num = (v: unknown, max = Number.MAX_SAFE_INTEGER, digits = 1): number | undefined => {
  if (typeof v !== "number" || !Number.isFinite(v) || v < 0) return undefined
  const f = 10 ** digits
  return Math.round(Math.min(v, max) * f) / f
}
const pressureOf = (v: unknown): LoadPressure =>
  typeof v === "string" && (LOAD_PRESSURES as ReadonlyArray<string>).includes(v)
    ? (v as LoadPressure)
    : "normal"
const RUN_ID = /^[A-Za-z0-9_-]{1,80}$/

/** A heartbeat's `load`, or null when it is absent or unreadable (the heartbeat itself always goes through). */
export const sanitizeRunnerLoad = (u: unknown): RunnerLoad | null => {
  if (!isObj(u)) return null
  const at = num(u.at, Number.MAX_SAFE_INTEGER, 0)
  const cores = num(u.cores, 4096)
  const memMb = num(u.memMb, 1e9, 0)
  const cpuPct = num(u.cpuPct, 100)
  const memPct = num(u.memPct, 100)
  if (
    at === undefined ||
    cores === undefined ||
    memMb === undefined ||
    cpuPct === undefined ||
    memPct === undefined
  )
    return null
  const opt = (k: string, v: number | undefined) => (v === undefined ? {} : { [k]: v })
  const runs = Array.isArray(u.runs)
    ? u.runs.slice(0, 32).flatMap((r) => {
        if (!isObj(r) || typeof r.runId !== "string" || !RUN_ID.test(r.runId)) return []
        const c = num(r.cpuPct, MAX_PCT)
        const m = num(r.rssMb, 1e9, 0)
        return c === undefined || m === undefined ? [] : [{ runId: r.runId, cpuPct: c, rssMb: m }]
      })
    : undefined
  return {
    at,
    cores,
    ...opt("vcpu", num(u.vcpu, 4096, 3)),
    memMb,
    cpuPct,
    memPct,
    pressure: pressureOf(u.pressure),
    ...opt("swapUsedMb", num(u.swapUsedMb, 1e9, 0)),
    ...opt("cpuWaitPct", num(u.cpuWaitPct, 100)),
    ...opt("throttledPct", num(u.throttledPct, 100)),
    ...opt("load1", num(u.load1, 1e6, 2)),
    ...(runs && runs.length > 0 ? { runs } : {}),
  } as RunnerLoad
}

/** A Final's `resources`, or null when absent or unreadable (the Final itself always goes through). */
export const sanitizeRunResources = (u: unknown): RunResources | null => {
  if (!isObj(u)) return null
  const samples = num(u.samples, 1e9, 0)
  const req = {
    cpuPeakPct: num(u.cpuPeakPct, MAX_PCT),
    cpuAvgPct: num(u.cpuAvgPct, MAX_PCT),
    rssPeakMb: num(u.rssPeakMb, 1e9, 0),
    rssAvgMb: num(u.rssAvgMb, 1e9, 0),
    cores: num(u.cores, 4096),
    memMb: num(u.memMb, 1e9, 0),
    machineCpuPeakPct: num(u.machineCpuPeakPct, 100),
    machineCpuAvgPct: num(u.machineCpuAvgPct, 100),
    machineMemPeakPct: num(u.machineMemPeakPct, 100),
  }
  if (samples === undefined || samples < 1 || Object.values(req).some((v) => v === undefined)) return null
  const opt = (k: string, v: number | undefined) => (v === undefined ? {} : { [k]: v })
  const host = typeof u.host === "string" && /^[a-z0-9-]{1,20}$/.test(u.host) ? { host: u.host } : {}
  return {
    samples,
    ...(req as { [K in keyof typeof req]: number }),
    ...opt("vcpu", num(u.vcpu, 4096, 3)),
    pressurePeak: pressureOf(u.pressurePeak),
    ...opt("swapPeakMb", num(u.swapPeakMb, 1e9, 0)),
    ...opt("throttledPct", num(u.throttledPct, 100)),
    ...opt("oomKills", num(u.oomKills, 1e6, 0)),
    ...opt("sharedCpuPeakPct", num(u.sharedCpuPeakPct, MAX_PCT)),
    ...host,
  } as RunResources
}
// #endregion sanitizers

/**
 * Decision 0149: the size and price of a Platform Runner profile's task sandbox (`RunnerProfile.container`). Display
 * and routing data for leads; the cloud fills it from the profile's `containerSize`, a runner never sends it.
 */
export const ProfileContainer = Schema.Struct({
  /** Cloudflare instance type ("standard-3"). */
  size: Schema.String,
  vcpu: Num,
  memoryMib: Num,
  /** List price per running hour with the CPU fully busy (an upper bound), in USD. */
  usdPerHour: Num,
  /** The part billed while it runs whatever the CPU does (memory and disk), in USD per hour. */
  usdPerHourFloor: Num,
  /** The profile's name as its admins gave it ("claude - high performance"). */
  name: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(120))),
})
export type ProfileContainer = typeof ProfileContainer.Type
